import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["title", "children", "connectDragRef", "handleClick", "allDay", "eventStyle"],
  _excluded2 = ["popoverOpen", "metadata", "allDay", "popoverProps", "popoverContent", "children", "eventStyle", "onPopoverOpenChange", "connectDragSource", "shouldTogglePopover", "title", "render"];
import { useState, useEffect, useContext, useCallback } from 'react';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import { DescriptionList } from 'foundations-components/transitional/components/DescriptionList';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { callIfPossible } from 'UIComponents/core/Functions';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { CalendarContext } from '../CalendarContextProvider';
import { styled, useTheme } from '../../lib/styles';
import { withTokenProps } from 'foundations-components/utils/withTokenProps';
import { H7 } from 'foundations-components/transitional/components/H7';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const filterCalendarProps = restParams => {
  const newParams = Object.assign({}, restParams);
  delete newParams.start;
  delete newParams.end;
  delete newParams.dragGroup;
  delete newParams.length;
  return newParams;
};
const EventShell = withTokenProps(styled.div.withConfig({
  displayName: "CalendarEvent__EventShell",
  componentId: "sc-1478vqy-0"
})(props => Object.assign({}, props.cssStyles)));
const CalendarEventInner = _ref => {
  let {
      title,
      children,
      connectDragRef,
      handleClick,
      allDay,
      eventStyle
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const renderDefaultContent = () => {
    return /*#__PURE__*/_jsx(TruncateString, {
      tooltip: false,
      paddingInline: "200",
      paddingBlockStart: "100",
      children: title
    });
  };
  const content = children || renderDefaultContent();
  return /*#__PURE__*/_jsx(EventShell, Object.assign({
    ref: connectDragRef,
    onClick: handleClick
  }, filterCalendarProps(rest), {
    display: "flex",
    borderRadius: "100",
    inlineSize: "100%",
    borderWidth: "100",
    borderStyle: "solid",
    borderColor: "border-interactive-default",
    backgroundColor: "fill-surface-default",
    color: "text-interactive-default",
    typography: "detail-100"
  }, allDay && {
    color: 'text-tertiary-default',
    typography: 'heading-25',
    paddingBlockStart: '0',
    borderColor: 'border-core-on-fill-default',
    backgroundColor: 'fill-accent-neutral-subtle-alt',
    fontWeight: allDay ? theme.typography['display-100'].fontWeight : theme.typography['body-300'].fontWeight
  }, {
    title: title
  }, theme.uiAddonCalendars.event, eventStyle, rest, {
    children: content
  }));
};
const UICalendarEvent = _ref2 => {
  let {
      popoverOpen,
      metadata,
      allDay,
      popoverProps,
      popoverContent,
      children,
      eventStyle,
      onPopoverOpenChange,
      connectDragSource,
      shouldTogglePopover,
      title,
      render
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const calendarContext = useContext(CalendarContext);
  const [isOpenFromContext, setIsOpenFromContext] = useState(false);
  const id = useUniqueId('ui-calendar-event');
  const updateOpenState = useCallback(openPopover => {
    setIsOpenFromContext(openPopover === id);
  }, [id]);
  useEffect(() => {
    const {
      subscribeToUpdates
    } = calendarContext || {};
    if (typeof subscribeToUpdates === 'function') {
      const unsubscribe = subscribeToUpdates(updateOpenState);
      return () => {
        unsubscribe();
      };
    }
  }, [calendarContext, updateOpenState]);
  const handlePopoverClose = useCallback(event => {
    const {
      setPopoverState
    } = calendarContext || {};
    if (isOpenFromContext) {
      // Set the context popover value only if
      // the popover was opened from the context
      callIfPossible(setPopoverState, id, false);
    }
    callIfPossible(onPopoverOpenChange, event, false);
  }, [calendarContext, id, isOpenFromContext, onPopoverOpenChange]);
  const handleClick = useCallback(event => {
    const {
      setPopoverState
    } = calendarContext || {};
    const isPopoverOpen = shouldTogglePopover ? !isOpenFromContext : true;
    callIfPossible(setPopoverState, id, isPopoverOpen);
    callIfPossible(onPopoverOpenChange, event, isPopoverOpen);
  }, [calendarContext, id, isOpenFromContext, onPopoverOpenChange, shouldTogglePopover]);
  const connectDragRef = useCallback(ref => {
    callIfPossible(connectDragSource, ref);
  }, [connectDragSource]);
  const renderPopoverBody = eventMetadata => {
    if (!eventMetadata) {
      return null;
    }
    return Object.keys(eventMetadata).map(dataKey => {
      return /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx("dt", {
          children: dataKey
        }), /*#__PURE__*/_jsx("dd", {
          children: eventMetadata[dataKey]
        })]
      }, dataKey);
    });
  };
  const getDefaultPopoverContent = () => {
    return {
      header: /*#__PURE__*/_jsx(H7, {
        children: title
      }),
      body: /*#__PURE__*/_jsx(DescriptionList, {
        paddingBlockEnd: "500",
        children: renderPopoverBody(metadata)
      })
    };
  };
  const actualPopoverContent = popoverContent || getDefaultPopoverContent();
  const event = render ? render({
    connectDragRef,
    handleClick
  }) : /*#__PURE__*/_jsx(CalendarEventInner, Object.assign({
    title: title,
    connectDragRef: connectDragRef,
    handleClick: handleClick,
    allDay: allDay,
    eventStyle: eventStyle
  }, rest, {
    children: children
  }));
  if (metadata || popoverContent) {
    return /*#__PURE__*/_jsx(Popover, Object.assign({}, popoverProps, {
      open: popoverOpen || isOpenFromContext,
      content: actualPopoverContent,
      onOpenChange: handlePopoverClose,
      children: event
    }));
  }
  return event;
};
export default UICalendarEvent;